import React from 'react';

import { AccountCircle, ListAlt } from '@material-ui/icons';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import PaperForm from 'ui/form/PaperForm';
import ButtonsForm from 'ui/form/ButtonsForm';
import SectionForm from 'ui/form/SectionForm';
import { useSelectDebounce } from 'utils/react';
import { naturalSort } from 'utils/sorting';
import AsyncSelect from 'react-select/async';
import classNames from 'classnames';
import SearchIcon from '@material-ui/icons/Search';

import LicensePart from '../parts/LicensePart';
import ContactPart from '../parts/ContactPart';

const useStyles = makeStyles(theme => ({
    searchIcon: {
        width: 36,
        color: 'hsl(0,0%,80%)',
        '&:hover': {
            color: 'hsl(0,0%,60%)',
        },
    },
    searchIconActive: {
        width: 36,
        color: 'hsl(0,0%,40%)',
    },
    searchField: {
        width: '50%',
        margin: `${theme.spacing()}px 0`,
    },
    errorText: {
        color: '#f44336',
        fontSize: '0.75rem',
        margin: '8px 14px 0px',
    },
}));

const toOptionsArr = options =>
    Object.keys(options).map(key => ({
        label: options[key],
        value: key,
    }));

const CompanyIntegrationAccountStep = ({
    errors,
    goBack,
    goNext,
    values,
    onChange,
    extra,
}) => {
    const classes = useStyles();
    const { config, service } = values;

    const loadOptions = async value => {
        if (!value) {
            return [];
        }

        const result = await config.serviceProvider.companiesExternalAgreements(
            service,
            getCompanyByName(values.business_name).value,
            value
        );

        return naturalSort(toOptionsArr(result), item => item.label);
    };

    const { setSearchTerm, setCallback } = useSelectDebounce(loadOptions);
    const handleLoadOptions = (term, callback) => {
        setCallback(() => {
            return callback;
        });
        setSearchTerm(term);
    };

    const getCompanyByName = name =>
        values.externalCompanies.find(item => item.label === name);

    return (
        <PaperForm title={extra.creating ? 'Create account' : 'Account'}>
            {extra.creating && (
                <SectionForm title="Primary contact" icon={AccountCircle}>
                    <ContactPart
                        values={values}
                        errors={errors}
                        onChange={onChange}
                        integration
                    />
                </SectionForm>
            )}

            <SectionForm title="License" icon={ListAlt}>
                <LicensePart
                    values={values}
                    errors={errors}
                    creating={extra.creating}
                    onChange={onChange}
                >
                    <AsyncSelect
                        className={classes.searchField}
                        value={values.agreement}
                        onChange={value => onChange({ agreement: value })}
                        placeholder={`${config.label} ${config.agreementLabel}`}
                        cacheOptions
                        loadOptions={loadOptions && handleLoadOptions}
                        isSearchable
                        styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                            control: base => ({
                                ...base,
                                height: 43,
                                borderColor: errors.agreement
                                    ? '#f00'
                                    : 'hsl(0,0%,80%)',
                            }),
                        }}
                        menuPosition="absolute"
                        noOptionsMessage={() => 'Start typing to search'}
                        menuPortalTarget={document.body}
                        components={{
                            DropdownIndicator,
                        }}
                    />
                    {errors.agreement && (
                        <Typography
                            variant="body2"
                            className={classes.errorText}
                        >
                            required
                        </Typography>
                    )}
                </LicensePart>
            </SectionForm>
            <ButtonsForm>
                <ButtonsForm.BackButton
                    label="Back"
                    onClick={goBack}
                    loading={values.loading}
                />
                <ButtonsForm.NextButton
                    label="Save"
                    onClick={goNext}
                    loading={values.loading}
                />
            </ButtonsForm>
        </PaperForm>
    );
};

const DropdownIndicator = ({ isFocused }) => {
    const classes = useStyles();
    const className = classNames({
        [classes.searchIcon]: !isFocused,
        [classes.searchIconActive]: isFocused,
    });
    return <SearchIcon className={className} />;
};

export default CompanyIntegrationAccountStep;
